<template>
  <div class="par-detail-page">
    <MainNavbar />

    <nav class="par-breadcrumb" aria-label="Breadcrumb">
      <router-link :to="{ name: storeRoute }" class="par-breadcrumb__link">
        <i class="fa-solid fa-wheelchair" aria-hidden="true" />
        Paralympic store
      </router-link>
      <span class="par-breadcrumb__sep" aria-hidden="true">/</span>
      <span class="par-breadcrumb__current">{{ item?.name || "Equipment" }}</span>
    </nav>

    <div v-if="loading" class="par-loading" aria-live="polite">
      <i class="fa-solid fa-spinner fa-spin fa-lg" aria-hidden="true" />
      <span>Loading details…</span>
    </div>

    <p v-else-if="loadError" class="par-empty par-detail-error" role="alert">
      {{ loadError }}
      <router-link :to="{ name: storeRoute }" class="par-back-link">Back to store</router-link>
    </p>

    <article v-else-if="item" class="par-detail">
      <div class="par-detail__gallery">
        <div class="par-detail__main-image">
          <img :src="activeImage" :alt="item.name" decoding="async" @error="onImageError" />
        </div>
        <div v-if="gallery.length > 1" class="par-detail__thumbs">
          <button
            v-for="(img, idx) in gallery"
            :key="idx"
            type="button"
            class="par-detail__thumb"
            :class="{ active: activeImage === img }"
            @click="activeImage = img"
          >
            <img :src="img" :alt="`${item.name} view ${idx + 1}`" loading="lazy" />
          </button>
        </div>
      </div>

      <div class="par-detail__info">
        <p class="par-detail__kicker">{{ item.category }} · {{ item.type }}</p>
        <h1 class="par-detail__title">{{ item.name }}</h1>
        <p v-if="item.brand" class="par-detail__brand">{{ item.brand }}</p>

        <div class="par-detail__badges">
          <span v-if="item.is_in_stock" class="par-detail__badge par-detail__badge--stock">
            <i class="fa-solid fa-circle-check" /> In stock
          </span>
          <span v-else class="par-detail__badge">Made to order</span>
          <span v-if="item.needs_fitting" class="par-detail__badge">Professional fitting</span>
          <span v-if="item.fitter_only" class="par-detail__badge par-detail__badge--fitter">
            Certified fitter required
          </span>
          <span v-if="item.returnable" class="par-detail__badge">Returnable</span>
        </div>

        <div class="par-detail__price">
          <span class="par-detail__price-now">Ksh {{ formatPrice(item.price) }}</span>
        </div>

        <p class="par-detail__sku">SKU: {{ item.sku }}</p>

        <p class="par-detail__short">{{ item.short_description }}</p>

        <div class="par-detail__actions">
          <button type="button" class="par-btn par-btn--primary par-btn--lg" @click="onMakeOrder">
            Make Order
          </button>
          <router-link :to="{ name: storeRoute }" class="par-btn par-btn--ghost par-btn--lg">
            Browse more
          </router-link>
        </div>

        <section v-if="item.description" class="par-detail__section">
          <h2>Description</h2>
          <p class="par-detail__desc">{{ item.description }}</p>
        </section>

        <section v-if="specRows.length" class="par-detail__section">
          <h2>Specifications</h2>
          <dl class="par-spec-list">
            <template v-for="row in specRows" :key="row.key">
              <dt>{{ row.key }}</dt>
              <dd>{{ row.value }}</dd>
            </template>
          </dl>
        </section>

        <section v-if="item.tags?.length" class="par-detail__section">
          <h2>Tags</h2>
          <div class="par-tag-list">
            <span v-for="tag in item.tags" :key="tag" class="par-tag">{{ tag }}</span>
          </div>
        </section>
      </div>
    </article>

    <Footer />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import Footer from "@/routes/thirdparty/Footer.vue";
import { buildApiUrl } from "@/config/api";
import globalToastMixin from "@/mixins/globalToastMixin";
import {
  PARALYMPIC_API,
  PARALYMPIC_STORE_ROUTE,
} from "@/routes/paralympic/constants";

const PLACEHOLDER = buildApiUrl("/static/images/product_images/placeholder.png");

export default {
  name: "ParalympicEquipmentDetail",
  components: { MainNavbar, Footer },
  mixins: [globalToastMixin],
  data() {
    return {
      item: null,
      loading: true,
      loadError: "",
      activeImage: PLACEHOLDER,
      storeRoute: PARALYMPIC_STORE_ROUTE,
    };
  },
  computed: {
    gallery() {
      if (!this.item) return [PLACEHOLDER];
      const imgs = [this.item.thumbnail, ...(this.item.images || [])].filter(Boolean);
      return imgs.length ? imgs : [PLACEHOLDER];
    },
    specRows() {
      const rows = [];
      const item = this.item;
      if (!item) return rows;
      if (item.weight_kg != null) rows.push({ key: "Weight", value: `${item.weight_kg} kg` });
      if (item.warranty_period) rows.push({ key: "Warranty", value: item.warranty_period });
      if (item.dimensions && typeof item.dimensions === "object") {
        Object.entries(item.dimensions).forEach(([key, value]) => {
          rows.push({ key: key.replace(/_/g, " "), value: String(value) });
        });
      }
      if (item.specs && typeof item.specs === "object") {
        Object.entries(item.specs).forEach(([key, value]) => {
          rows.push({ key: key.replace(/_/g, " "), value: String(value) });
        });
      }
      return rows;
    },
  },
  watch: {
    "$route.params.slug": {
      immediate: true,
      handler() {
        this.fetchDetail();
      },
    },
  },
  methods: {
    formatPrice(value) {
      return Number(value || 0).toLocaleString("en-KE");
    },
    onImageError() {
      this.activeImage = PLACEHOLDER;
    },
    async fetchDetail() {
      const slug = this.$route.params.slug;
      if (!slug) {
        this.loadError = "Invalid equipment link.";
        this.loading = false;
        return;
      }

      this.loading = true;
      this.loadError = "";
      try {
        const res = await fetch(buildApiUrl(PARALYMPIC_API.detail(slug)));
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || "Equipment not found.");
        }
        this.item = data;
        this.activeImage = this.gallery[0];
      } catch (err) {
        this.item = null;
        this.loadError = err.message || "Could not load equipment.";
      } finally {
        this.loading = false;
      }
    },
    onMakeOrder() {
      this.showToast(
        "Orders coming soon",
        `Ordering for “${this.item?.name}” will open in a future update.`,
        "info",
      );
    },
  },
};
</script>

<style scoped src="@/components/paralympic/paralympicStore.css"></style>

<style scoped>
.par-detail-page {
  min-height: 100vh;
  background: var(--background-color1);
  padding-top: 72px;
}

.par-breadcrumb {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.85rem 1rem 0;
  font-size: 0.85rem;
  color: var(--text-color2);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.par-breadcrumb__link {
  color: var(--border-color-hover);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.par-breadcrumb__link:hover {
  text-decoration: underline;
}

.par-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.25rem, 4vw, 2.5rem);
}

.par-detail__gallery {
  background: var(--background-color2);
  border-radius: 16px;
  padding: 1rem;
  border: 1px solid var(--background-color3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.par-detail__main-image {
  position: relative;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color1);
  border-radius: 12px;
  overflow: hidden;
}

.par-detail__main-image img {
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  padding: 1rem;
}

.par-detail__thumbs {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.par-detail__thumb {
  flex: 0 0 72px;
  height: 72px;
  border-radius: 8px;
  border: 2px solid transparent;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: var(--background-color1);
}

.par-detail__thumb.active {
  border-color: var(--border-color-hover);
}

.par-detail__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.par-detail__info {
  background: var(--background-color2);
  border-radius: 16px;
  padding: clamp(1rem, 3vw, 1.5rem);
  border: 1px solid var(--background-color3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.par-detail__kicker {
  margin: 0 0 0.35rem;
  font-size: 0.78rem;
  color: var(--text-color2);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.par-detail__title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.35rem, 4vw, 1.85rem);
  color: var(--text-color1);
  line-height: 1.2;
}

.par-detail__brand {
  margin: 0 0 0.75rem;
  font-weight: 600;
  color: var(--border-color-hover);
}

.par-detail__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

.par-detail__badge {
  font-size: 0.72rem;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  background: var(--background-color1);
  color: var(--text-color2);
  border: 1px solid var(--background-color3);
}

.par-detail__badge--stock {
  color: #2e7d32;
  border-color: rgba(46, 125, 50, 0.35);
}

.par-detail__badge--fitter {
  background: rgba(var(--border-color-hover-rgb, 37, 99, 235), 0.12);
  color: var(--border-color-hover);
  border-color: transparent;
}

.par-detail__price {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
}

.par-detail__price-now {
  font-size: clamp(1.2rem, 4vw, 1.55rem);
  font-weight: 800;
  color: var(--border-color-hover);
}

.par-detail__sku {
  margin: 0 0 0.75rem;
  font-size: 0.78rem;
  color: var(--text-color2);
}

.par-detail__short {
  margin: 0 0 1.25rem;
  color: var(--text-color2);
  line-height: 1.55;
}

.par-detail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-bottom: 1.5rem;
}

.par-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.25rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: none;
  border: none;
  transition: transform 0.2s ease;
}

.par-btn--lg {
  min-width: 140px;
}

.par-btn--primary {
  background: var(--border-color-hover);
  color: var(--background-color2);
}

.par-btn--ghost {
  background: transparent;
  color: var(--text-color1);
  border: 1px solid var(--text-color1);
}

.par-btn:hover {
  transform: translateY(-2px);
}

.par-detail__section {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--background-color3);
}

.par-detail__section h2 {
  margin: 0 0 0.65rem;
  font-size: 1rem;
  color: var(--text-color1);
}

.par-detail__desc {
  margin: 0;
  white-space: pre-line;
  line-height: 1.6;
  color: var(--text-color2);
}

.par-spec-list {
  display: grid;
  grid-template-columns: minmax(100px, 35%) 1fr;
  gap: 0.45rem 1rem;
  margin: 0;
}

.par-spec-list dt {
  margin: 0;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-color1);
  text-transform: capitalize;
}

.par-spec-list dd {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-color2);
}

.par-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.par-tag {
  font-size: 0.75rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: var(--background-color1);
  color: var(--text-color2);
}

.par-detail-error {
  max-width: 600px;
  margin: 2rem auto;
}

.par-back-link {
  display: inline-block;
  margin-top: 0.75rem;
  color: var(--border-color-hover);
}

@media (max-width: 900px) {
  .par-detail {
    grid-template-columns: 1fr;
  }
}
</style>
